import BackgroundColors from '@root/joinroot.com/src/utils/background-colors';
import LinkButton from '@root/joinroot.com/src/components/buttons/link-button';
import PropTypes from '@root/vendor/prop-types';
import Responsive from '@root/core/src/utils/responsive';
import { Eyebrow, H1Display, Subhead } from '@root/joinroot.com/src/components/text';
import { StyleSheet } from '@root/core/src/utils/styles';
import { VERTICAL_IMAGE_ALIGNMENT } from '@root/joinroot.com/src/constants/constants';

export default function HeroImageRight({
  anchor,
  buttons,
  eyebrow,
  form,
  image,
  imageAlt,
  headline,
  subhead,
  backgroundColor = BackgroundColors.cmsValues.WHITE,
  hideImageOnMobile = false,
  verticalImageAlignment,
  buttonComponent = LinkButton,
}) {
  const ButtonComponent = buttonComponent;

  const renderButtons = buttons.length > 0;

  return (
    <section
      css={[
        stylesBase.heroSection,
        BackgroundColors.getBackgroundStyle(backgroundColor),
        hideImageOnMobile && stylesHideImageOnMobile.heroSection,
        verticalImageAlignment === VERTICAL_IMAGE_ALIGNMENT.BOTTOM && imageAlignBottom.heroSection,
      ]}
      id={anchor}
    >
      <div className={'wrap'}>

        <div className={'container_content'}>
          {eyebrow && <Eyebrow className={'eyebrow'}>{eyebrow}</Eyebrow>}
          {headline && <H1Display className={'headline'}>{headline}</H1Display>}
          {subhead && <Subhead className={'subhead'}>{subhead}</Subhead>}
          {renderButtons && (
            <div className={'container_cta'}>
              {buttons.map((btn, i) => (
                <ButtonComponent
                  buttonStyles={buttonStyles}
                  key={i}
                  onClick={btn.onClick}
                  to={btn.link}
                  type={btn.type}
                >
                  {btn.text}
                </ButtonComponent>
              ))}
            </div>
          )}
          {form}
        </div>

        <div className={'container_image'}>
          <img
            alt={imageAlt}
            src={image}
          />
        </div>

      </div>
    </section >
  );
}

HeroImageRight.propTypes = {
  anchor: PropTypes.string,
  backgroundColor: PropTypes.oneOf(BackgroundColors.cmsValueList),
  buttonComponent: PropTypes.func,
  buttons: PropTypes.arrayOf(PropTypes.object),
  eyebrow: PropTypes.string,
  form: PropTypes.node,
  headline: PropTypes.string.isRequired,
  hideImageOnMobile: PropTypes.bool,
  image: PropTypes.string.isRequired,
  imageAlt: PropTypes.string,
  subhead: PropTypes.string,
  verticalImageAlignment: PropTypes.string,
};

const stylesHideImageOnMobile = StyleSheet.create({
  heroSection: {

    '.wrap': {

      '.container_content': {
        padding: '80px 20px',

        ...Responsive.lg({
          padding: 0,
        }),
      },

      '.container_image': {
        display: 'none',

        ...Responsive.lg({
          display: 'flex',
        }),
      },
    },
  },
});

const imageAlignBottom = StyleSheet.create({
  heroSection: {
    '.wrap .container_image': {
      ...Responsive.md({
        alignItems: 'flex-end',
      }),
    },
  },
});

const stylesBase = StyleSheet.create({
  heroSection: {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'white',

    '.wrap': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
      maxWidth: 1200,

      ...Responsive.lg({
        position: 'relative',
        flexDirection: 'row',
        margin: '0 0 0 30px',
        minHeight: 650,
        maxHeight: 700,
      }),

      '.container_content': {
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        alignItems: 'center',
        textAlign: 'center',
        padding: '80px 20px 20px',

        ...Responsive.lg({
          position: 'absolute',
          zIndex: 2,
          top: 0,
          left: 0,
          width: '45%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          textAlign: 'left',
          padding: 0,
        }),

        '.eyebrow': {
          maxWidth: 350,
        },

        '.headline, .subhead': {
          maxWidth: 550,

          ...Responsive.md({
            maxWidth: 800,
          }),
        },

        '.eyebrow, .headline, .subhead': {
          marginBottom: 12,
        },

        '.container_cta': {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          margin: '30px -10px 0', // -10px accounts for margin on cta elements within.
          width: '100%',
          maxWidth: 260,

          ...Responsive.md({
            flexDirection: 'row',
            maxWidth: 480,
          }),

          ...Responsive.lg({
            justifyContent: 'flex-start',
          }),
        },
      },

      '.container_image': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        maxHeight: 700,

        ...Responsive.lg({
          margin: '0 0 0 35%',
          height: '100%',
        }),

        'img': {
          maxWidth: '100%',
          maxHeight: '100%',
        },
      },
    },
  },
});

const buttonStyles = {
  margin: 10,
};
